import {
  Autocomplete,
  Box,
  Checkbox,
  Grid,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import React, { useEffect, useMemo, useState } from "react";
import {
  BackButton,
  Button,
  ErrorMessage,
  Heading,
  Input,
  Select,
  UploadImage,
} from "../../components/Common";
import { useLocation, useNavigate } from "react-router-dom";
import { bloodGroupList, countryList } from "../../helper";
import { useMessage } from "../../components/Common/Message/MessageContext";
import {
  useGetCityListQuery,
  useGetStateListQuery,
} from "../../services/Location";
import {
  useAddNewAdminMutation,
  useUpdateAdminMutation,
  useGetAdminDetailQuery,
} from "../../services/Admin";
import {
  useGetPincodeByCityQuery,
  useGetExtraPincodeByCityQuery,
} from "../../services/SubAdmin";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddAdmin = () => {
  const { showMessage } = useMessage();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState([]);
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(null);
  const [city, setCity] = useState(null);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [aadharImage, setAadharImage] = useState([]);
  const [bloodGroup, setBloodGroup] = useState(null);
  const [formValues, setFormValues] = useState({
    name: "",
    mobile: "",
    email: "",
    guardianName: "",
    guardianMobile: "",
    address: "",
    panNo: "",
    dob: "",
  });
  const [errorMessages, setErrorMessages] = useState({});
  const [profileErrorMessage, setProfileErrorMessage] = useState("");
  const [addharErrorMessage, setAddharErrorMessage] = useState("");
  const [orderDialog, setOrderDialog] = useState({
    open: false,
    pincode: '',
    orders: [],
    totalOrders: 0,
  });

  const {
    data: cityLst,
    error: cityError,
    isLoading: cityLoading,
  } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;
  const {
    data: stateLst,
    error: stateError,
    isLoading: stateLoading,
  } = useGetStateListQuery();
  const stateList = stateLst?.data;

  const editingAdminId = state?.id || null;

  const { data: extraPincodeData, isLoading: extraPincodeLoading } =
    useGetExtraPincodeByCityQuery(city, {
      skip: !city,
      refetchOnMountOrArgChange: true,
    });

  const { data: cityPincodeOwnershipData, isLoading: cityPincodeLoading } =
    useGetPincodeByCityQuery(city, {
      skip: !city,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });

  const pincodeOwnershipMap = useMemo(() => {
    const map = {};
    const list =
      cityPincodeOwnershipData?.data ||
      cityPincodeOwnershipData?.pinCodeData ||
      [];
    list.forEach((item) => {
      const pin = item.pincode || item.pinCode;
      if (pin) {
        map[pin] = item.adminId || null;
      }
    });
    return map;
  }, [cityPincodeOwnershipData]);

  const cityPincodes = useMemo(() => {
    if (!city) return [];

    const extraPins = extraPincodeData?.data || [];

    return extraPins.map((pin) => ({
      pincode: pin.pincode,
      adminId: pincodeOwnershipMap[pin.pincode] ?? null,
    }));
  }, [city, extraPincodeData, pincodeOwnershipMap]);

  const handleStateChange = (e) => setStates(e.target.value);
  const handleCountryChange = (e) => setCountry(e.target.value);
  const handleCityChange = (e) => setCity(e.target.value);
  const handleBloodGroupChange = (e) => setBloodGroup(e.target.value);

  const handleCloseOrderDialog = () => {
    // Re-add the blocked pincode back to selected pincodes
    if (orderDialog.pincode && !selectedPincodes.includes(orderDialog.pincode)) {
      setSelectedPincodes(prev => [...prev, orderDialog.pincode]);
    }

    setOrderDialog({
      open: false,
      pincode: '',
      orders: [],
      totalOrders: 0,
    });
  };

  const [addAdmin] = useAddNewAdminMutation();
  const [updateAdmin] = useUpdateAdminMutation();

  const { data: adminDetailData } = useGetAdminDetailQuery(state?.id, {
    skip: !state?.id,
    refetchOnMountOrArgChange: true,
  });
  const adminPincodesFromBackend = useMemo(() => {
    return adminDetailData?.data?.pincodes || [];
  }, [adminDetailData, state?.id]);

  useEffect(() => {
    if (!city || !cityPincodes.length) {
      setSelectedPincodes([]);
      return;
    }

    if (!state?.id) {
      const availablePinsForCity = cityPincodes
        .filter((p) => !p.adminId)
        .map((p) => p.pincode);
      setSelectedPincodes(availablePinsForCity);
    }
  }, [city, cityPincodes, state?.id]);

  useEffect(() => {
    if (!state?.id) return;

    if (city && cityPincodes.length > 0) {
      if (adminPincodesFromBackend.length > 0) {
        const validPincodes = adminPincodesFromBackend.filter((pin) =>
          cityPincodes.some((cp) => cp.pincode === pin)
        );
        setSelectedPincodes(validPincodes);
      } else {
        setSelectedPincodes([]);
      }
    }
  }, [adminPincodesFromBackend, state?.id, city, cityPincodes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validateField = (field, fieldName) => {
    if (!field) {
      setErrorMessages((prevState) => ({
        ...prevState,
        [fieldName]: `${fieldName} is a mandatory field.`,
      }));
      return true;
    }
    setErrorMessages((prevState) => ({ ...prevState, [fieldName]: "" }));
    return false;
  };

  const handleFormSubmit = async () => {
    setErrorMessages({});
    setProfileErrorMessage("");
    setAddharErrorMessage("");

    let hasError = false;

    const hasProfileImage =
      profileImage instanceof File ||
      (Array.isArray(profileImage) &&
        profileImage.length > 0 &&
        profileImage[0] instanceof File);

    const hasAadharImage =
      aadharImage instanceof File ||
      (Array.isArray(aadharImage) &&
        aadharImage.length > 0 &&
        aadharImage[0] instanceof File);

    if (!hasProfileImage && !state?.id) {
      setProfileErrorMessage("Please select a profile image to upload.");
      hasError = true;
    }

    if (!hasAadharImage && !state?.id) {
      setAddharErrorMessage("Please select an aadhar image to upload.");
      hasError = true;
    }

    const nameError = validateField(formValues.name, "name");
    const mobileError = validateField(formValues.mobile, "mobile");
    const emailError = validateField(formValues.email, "email");
    const guardianNameError = validateField(
      formValues.guardianName,
      "guardianName"
    );
    const guardianMobileError = validateField(
      formValues.guardianMobile,
      "guardianMobile"
    );
    const countryError = validateField(country, "country");
    const stateError = validateField(states, "state");
    const cityError = validateField(city, "city");
    const addressError = validateField(formValues.address, "address");
    const panNoError = validateField(formValues.panNo, "panNo");
    const dobError = validateField(formValues.dob, "dob");
    const bloodGroupError = validateField(bloodGroup, "bloodGroup");

    const hasCityPincodes = city && cityPincodes.length > 0;
    let pincodeError = false;
    if (hasCityPincodes && selectedPincodes.length === 0) {
      setErrorMessages((prevState) => ({
        ...prevState,
        pincode: "Please select at least one pincode for this city.",
      }));
      pincodeError = true;
    } else {
      setErrorMessages((prevState) => ({ ...prevState, pincode: "" }));
    }

    if (
      nameError ||
      mobileError ||
      emailError ||
      guardianNameError ||
      guardianMobileError ||
      countryError ||
      stateError ||
      cityError ||
      addressError ||
      hasError ||
      panNoError ||
      dobError ||
      bloodGroupError ||
      pincodeError
    ) {
      showMessage("error", "Please fill all mandatory fields");
      return;
    }

    const formData = new FormData();

    if (profileImage instanceof File) {
      formData.append("profileImage", profileImage);
    } else if (
      Array.isArray(profileImage) &&
      profileImage.length > 0 &&
      profileImage[0] instanceof File
    ) {
      formData.append("profileImage", profileImage[0]);
    }

    if (aadharImage instanceof File) {
      formData.append("aadharImage", aadharImage);
    } else if (
      Array.isArray(aadharImage) &&
      aadharImage.length > 0 &&
      aadharImage[0] instanceof File
    ) {
      formData.append("aadharImage", aadharImage[0]);
    }

    formData.append("bloodGroup", bloodGroup);
    formData.append("name", formValues.name);
    formData.append("mobile", formValues.mobile);
    formData.append("email", formValues.email);
    formData.append("guardianName", formValues.guardianName);
    formData.append("guardianMobile", formValues.guardianMobile);
    formData.append("country", country);
    formData.append("state", states);
    formData.append("city", city);
    formData.append("address", formValues.address);
    formData.append("pincode", JSON.stringify(selectedPincodes));
    formData.append("panNo", formValues.panNo);
    formData.append("dob", formValues.dob);

    try {
      let response;

      if (state?.id) {
        response = await updateAdmin({ formData, id: state.id });
      } else {
        response = await addAdmin(formData);
      }

      if (response.error) {
        const errorData = response.error?.data;

        // Check for blocking orders in error response
        if (errorData?.blockingOrders && errorData.blockingOrders.length > 0) {
          const firstBlocking = errorData.blockingOrders[0];
          setOrderDialog({
            open: true,
            pincode: firstBlocking.pincode,
            orders: firstBlocking.orders.map(orderNumber => ({ orderNumber })),
            totalOrders: firstBlocking.orders.length,
          });
        } else {
          const errorMessage = errorData?.message || "Failed to save admin";
          showMessage("error", errorMessage);
        }
      } else if (response.data) {
        const { status, message } = response.data;
        if (status) {
          showMessage("success", message || "Admin saved successfully");
          navigate("/admin");
        } else {
          // Check for blocking orders in unsuccessful response
          if (response.data?.blockingOrders && response.data.blockingOrders.length > 0) {
            const firstBlocking = response.data.blockingOrders[0];
            setOrderDialog({
              open: true,
              pincode: firstBlocking.pincode,
              orders: firstBlocking.orders.map(orderNumber => ({ orderNumber })),
              totalOrders: firstBlocking.orders.length,
            });
          } else {
            showMessage("error", message || "Failed to save admin");
          }
        }
      }
    } catch (error) {
      console.error("Failed to save the admin:", error);

      // Handle error with order details
      if (error?.data?.blockingOrders && error.data.blockingOrders.length > 0) {
        const firstBlocking = error.data.blockingOrders[0];
        setOrderDialog({
          open: true,
          pincode: firstBlocking.pincode,
          orders: firstBlocking.orders.map(orderNumber => ({ orderNumber })),
          totalOrders: firstBlocking.orders.length,
        });
      } else {
        showMessage("error", "An unexpected error occurred");
      }
    }
  };

  useEffect(() => {
    if (state) {
      setFormValues({
        name: state?.data?.name,
        mobile: state?.data?.mobile,
        email: state?.data?.email,
        guardianName: state?.data?.guardianName,
        guardianMobile: state?.data?.guardianMobile,
        address: state?.data?.address,
        panNo: state?.data?.panNo,
        dob: new Date(state?.data?.dob).toISOString().split("T")[0],
        id: state.id,
      });
      setProfileImage(state?.data?.profileImage);
      setAadharImage(state?.data?.aadharImage);
      setBloodGroup(state?.data?.bloodGroup);
      setStates(state?.data?.state);
      setCity(state?.data?.city);
      setSelectedPincodes([]);
    }
  }, [state]);

  return (
    <>
      <Stack flexDirection={"row"} gap={1} mb={1}>
        <BackButton />
        <Heading head="Add Admin" />
      </Stack>
      <Box className="card_container">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              User Photo
            </Typography>
            <UploadImage files={profileImage} setFiles={setProfileImage} />
            <ErrorMessage message={profileErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
            />
            {errorMessages.name && (
              <ErrorMessage message={errorMessages.name} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Mobile"
              type="number"
              name="mobile"
              value={formValues.mobile}
              onChange={handleInputChange}
            />
            {errorMessages.mobile && (
              <ErrorMessage message={errorMessages.mobile} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Email"
              type="email"
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
            />
            {errorMessages.email && (
              <ErrorMessage message={errorMessages.email} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Guardian Name"
              name="guardianName"
              value={formValues.guardianName}
              onChange={handleInputChange}
            />
            {errorMessages.guardianName && (
              <ErrorMessage message={errorMessages.guardianName} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="Guardian Mobile"
              name="guardianMobile"
              value={formValues.guardianMobile}
              onChange={handleInputChange}
            />
            {errorMessages.guardianMobile && (
              <ErrorMessage message={errorMessages.guardianMobile} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input
              labelinput="Pan Card No"
              name="panNo"
              value={formValues.panNo}
              onChange={handleInputChange}
            />
            {errorMessages.panNo && (
              <ErrorMessage message={errorMessages.panNo} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Input
              labelinput="Address"
              name="address"
              value={formValues.address}
              onChange={handleInputChange}
            />
            {errorMessages.address && (
              <ErrorMessage message={errorMessages.address} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={countryList}
              labelinput="Country"
              name="workType"
              value={country}
              onChange={handleCountryChange}
            />
            {errorMessages.country && (
              <ErrorMessage message={errorMessages.country} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={stateLoading ? [] : stateList}
              labelinput="State"
              name="workType"
              value={states}
              onChange={handleStateChange}
            />
            {errorMessages.state && (
              <ErrorMessage message={errorMessages.state} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select
              selectList={cityLoading ? [] : cityList}
              labelinput="City"
              name="workType"
              value={city}
              onChange={handleCityChange}
            />
            {errorMessages.city && (
              <ErrorMessage message={errorMessages.city} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              Select Pincodes
            </Typography>
            <Autocomplete
              multiple
              id="admin-pincode-select"
              options={cityPincodes}
              loading={extraPincodeLoading || cityPincodeLoading}
              disableCloseOnSelect
              getOptionLabel={(option) => option.pincode || ""}
              isOptionEqualToValue={(option, value) =>
                (option?.pincode || "") === (value?.pincode || "")
              }
              value={cityPincodes.filter((p) =>
                selectedPincodes.includes(p.pincode)
              )}
              onChange={(event, newValue) => {
                const pins = newValue.map((item) => item.pincode);
                setSelectedPincodes(pins);
                setErrorMessages((prev) => ({ ...prev, pincode: "" }));
              }}
              getOptionDisabled={(option) => {
                if (!option.adminId) return false;
                if (editingAdminId && option.adminId === editingAdminId)
                  return false;
                return true;
              }}
              renderOption={(props, option, { selected }) => {
                const isOwnedByOther =
                  !!option.adminId &&
                  (!editingAdminId || option.adminId !== editingAdminId);

                const { key, ...optionProps } = props;

                return (
                  <li
                    key={key}
                    {...optionProps}
                    style={{
                      backgroundColor: isOwnedByOther ? "#e3f2fd" : "#ffffff",
                      opacity: isOwnedByOther ? 0.7 : 1,
                    }}
                  >
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                      disabled={isOwnedByOther}
                    />

                    <span>{option.pincode}</span>

                    {isOwnedByOther && (
                      <Typography
                        component="span"
                        fontSize="11px"
                        color="text.secondary"
                        ml={1}
                      >
                        (Already assigned)
                      </Typography>
                    )}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search & select pincodes" />
              )}
            />
            {errorMessages.pincode && (
              <ErrorMessage message={errorMessages.pincode} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography
              fontSize={{ xs: "12px", sm: "13px" }}
              fontWeight={500}
              mb={"2px"}
            >
              Upload Aadhar Card
            </Typography>
            <UploadImage files={aadharImage} setFiles={setAadharImage} />
            <ErrorMessage message={addharErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select
              selectList={bloodGroupList}
              labelinput="Blood Group"
              name="bloodGroup"
              value={bloodGroup}
              onChange={handleBloodGroupChange}
            />
            {errorMessages.bloodGroup && (
              <ErrorMessage message={errorMessages.bloodGroup} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input
              labelinput="User DOB"
              name="dob"
              type="date"
              value={formValues.dob}
              onChange={handleInputChange}
            />
            {errorMessages.dob && <ErrorMessage message={errorMessages.dob} />}
          </Grid>
          <Grid item xs={12}>
            <Stack flexDirection={"row"} gap={1}>
              <Button buttonName="save" onClick={handleFormSubmit} />
              <Button
                buttonName="cancel"
                color="white"
                onClick={() => navigate("/admin")}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* ORDER ASSOCIATION WARNING DIALOG */}
      <Dialog
        open={orderDialog.open}
        onClose={handleCloseOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Cannot Remove Pincode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pincode <strong>{orderDialog.pincode}</strong> is associated with{' '}
            <strong>{orderDialog.totalOrders}</strong> active order(s) and cannot be removed from this admin.
          </DialogContentText>

          {orderDialog.orders && orderDialog.orders.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Associated Orders:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order Number</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDialog.orders.map((order, index) => (
                      <TableRow key={index}>
                        <TableCell>{order.orderNumber}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            You can only remove pincodes from admins if the associated orders have status:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Upcoming/Placed</li>
              <li>Assigned to Technician</li>
              <li>Completed</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button buttonName="Close" onClick={handleCloseOrderDialog} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAdmin;